import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, LinkIcon, UnlinkIcon, SaveIcon, XIcon, ChevronUpIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
import { getDefaultCurrency } from '../../utils/appSettings';
export function CreditNoteEntry({
  onSave,
  locale = 'en-US',
  defaultCurrency
}) {
  // Initialize all state variables first, before any useEffect hooks
  const [creditNote, setCreditNote] = useState({
    id: '',
    vendor: '',
    vendorId: '',
    creditNoteNumber: '',
    creditNoteDate: '',
    currency: defaultCurrency || getDefaultCurrency(),
    description: '',
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: '',
    linkedInvoices: [],
    lines: [{
      id: 1,
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      amount: 0,
      linkedInvoiceId: null,
      linkedLineId: null,
      linkedLineDescription: null
    }]
  });
  const [errors, setErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    vendor: true,
    lines: true,
    linkedInvoices: true
  });
  const [showInvoiceSelector, setShowInvoiceSelector] = useState(false);
  const [showLineItemSelector, setShowLineItemSelector] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedInvoiceForLineItems, setSelectedInvoiceForLineItems] = useState(null);
  // Sample data
  const vendors = [{
    id: 'V001',
    name: 'Office Depot'
  }, {
    id: 'V002',
    name: 'Dell Technologies'
  }, {
    id: 'V003',
    name: 'Server Solutions Inc.'
  }];
  const taxCodes = [{
    code: 'STD',
    name: 'Standard Rate',
    rate: 10
  }, {
    code: 'RED',
    name: 'Reduced Rate',
    rate: 5
  }, {
    code: 'ZERO',
    name: 'Zero Rate',
    rate: 0
  }, {
    code: 'EXM',
    name: 'Exempt',
    rate: 0
  }];
  const sampleInvoices = [{
    id: 'INV-2023-001',
    number: 'INV-2023-001',
    date: '2023-10-15',
    amount: 1250.75,
    status: 'Approved',
    vendor: 'Office Depot',
    vendorId: 'V001',
    lines: [{
      id: 1,
      description: 'Office Supplies - Paper',
      quantity: 10,
      unitPrice: 25.5,
      amount: 255.0
    }, {
      id: 2,
      description: 'Office Supplies - Pens',
      quantity: 50,
      unitPrice: 1.25,
      amount: 62.5
    }]
  }, {
    id: 'INV-2023-002',
    number: 'INV-2023-002',
    date: '2023-10-18',
    amount: 4300.0,
    status: 'Approved',
    vendor: 'Dell Technologies',
    vendorId: 'V002',
    lines: [{
      id: 1,
      description: 'Laptop - XPS 15',
      quantity: 2,
      unitPrice: 1800.0,
      amount: 3600.0
    }, {
      id: 2,
      description: 'Monitor - 27"',
      quantity: 2,
      unitPrice: 350.0,
      amount: 700.0
    }]
  }, {
    id: 'INV-2023-003',
    number: 'INV-2023-003',
    date: '2023-09-15',
    amount: 7500.0,
    status: 'Approved',
    vendor: 'Dell Technologies',
    vendorId: 'V002',
    lines: [{
      id: 1,
      description: 'Server - PowerEdge R740',
      quantity: 1,
      unitPrice: 6000.0,
      amount: 6000.0
    }, {
      id: 2,
      description: 'Server RAM Upgrade',
      quantity: 4,
      unitPrice: 375.0,
      amount: 1500.0
    }]
  }, {
    id: 'INV-2023-004',
    number: 'INV-2023-004',
    date: '2023-09-28',
    amount: 12350.0,
    status: 'Approved',
    vendor: 'Server Solutions Inc.',
    vendorId: 'V003',
    lines: [{
      id: 1,
      description: 'Network Infrastructure Setup',
      quantity: 1,
      unitPrice: 8500.0,
      amount: 8500.0
    }, {
      id: 2,
      description: 'Managed Services - 3 months',
      quantity: 3,
      unitPrice: 1250.0,
      amount: 3750.0
    }, {
      id: 3,
      description: 'Hardware Installation',
      quantity: 1,
      unitPrice: 100.0,
      amount: 100.0
    }]
  }];
  // Function to perform the search
  const performSearch = () => {
    setIsSearching(true);
    // Simulate a slight delay to show the searching state
    setTimeout(() => {
      const searchTermLower = searchTerm.toLowerCase().trim();
      // If search term is empty, show all available invoices
      if (!searchTermLower) {
        setFilteredInvoices(availableInvoices);
        setIsSearching(false);
        return;
      }
      const filtered = availableInvoices.filter(invoice => invoice.number.toLowerCase().includes(searchTermLower) || invoice.vendor.toLowerCase().includes(searchTermLower) || invoice.id && invoice.id.toLowerCase().includes(searchTermLower));
      setFilteredInvoices(filtered);
      setIsSearching(false);
    }, 300);
  };
  // Update filtered invoices when the modal opens
  useEffect(() => {
    if (showInvoiceSelector) {
      performSearch();
    }
  }, [showInvoiceSelector]);
  // Toggle section expansion
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Handle form field changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setCreditNote({
      ...creditNote,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Add a new line item
  const addLineItem = () => {
    const newId = creditNote.lines.length > 0 ? Math.max(...creditNote.lines.map(item => item.id)) + 1 : 1;
    const newLine = {
      id: newId,
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      amount: 0,
      linkedInvoiceId: null,
      linkedLineId: null,
      linkedLineDescription: null
    };
    setCreditNote(prevState => ({
      ...prevState,
      lines: [...prevState.lines, newLine]
    }));
  };
  // Remove a line item
  const removeLineItem = id => {
    const updatedLines = creditNote.lines.filter(line => line.id !== id);
    setCreditNote(prevState => ({
      ...prevState,
      lines: updatedLines,
      amount: updatedLines.reduce((sum, line) => sum + (line.amount || 0), 0)
    }));
  };
  // Handle line item changes
  const handleLineItemChange = (index, field, value) => {
    const updatedLines = [...creditNote.lines];
    if (field === 'taxCode') {
      // Find the tax rate for the selected tax code
      const selectedTaxCode = taxCodes.find(tax => tax.code === value);
      const taxRate = selectedTaxCode ? selectedTaxCode.rate : 0;
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value,
        taxRate: taxRate
      };
      // Recalculate tax amount
      const lineValue = updatedLines[index].quantity * updatedLines[index].unitPrice;
      updatedLines[index].taxAmount = calculateTaxAmount(lineValue, taxCodes.find(tax => tax.code === value));
      updatedLines[index].amount = lineValue + updatedLines[index].taxAmount;
    } else if (field === 'quantity' || field === 'unitPrice') {
      // Update the field value
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: typeof value === 'number' ? value : parseFloat(value) || 0
      };
      // Recalculate amount and tax
      const lineValue = updatedLines[index].quantity * updatedLines[index].unitPrice;
      updatedLines[index].taxAmount = calculateTaxAmount(lineValue, taxCodes.find(tax => tax.code === updatedLines[index].taxCode));
      updatedLines[index].amount = lineValue + updatedLines[index].taxAmount;
    } else {
      // For other fields, just update the value
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value
      };
    }
    // Update state with new lines
    setCreditNote({
      ...creditNote,
      lines: updatedLines,
      amount: updatedLines.reduce((sum, line) => sum + (line.amount || 0), 0)
    });
  };
  // Open invoice selector to link an invoice
  const openInvoiceSelector = () => {
    // Reset search
    setSearchTerm('');
    // Get invoices that match the vendor and aren't already linked
    const vendorInvoices = sampleInvoices.filter(inv => inv.vendorId === creditNote.vendorId).filter(inv => !creditNote.linkedInvoices.some(linked => linked.id === inv.id));
    setAvailableInvoices(vendorInvoices);
    setFilteredInvoices(vendorInvoices);
    setShowInvoiceSelector(true);
  };
  // Link an invoice to the credit note
  const linkInvoice = invoice => {
    // Check if invoice is already linked
    if (creditNote.linkedInvoices.some(inv => inv.id === invoice.id)) {
      return;
    }
    setCreditNote(prevState => ({
      ...prevState,
      linkedInvoices: [...prevState.linkedInvoices, invoice]
    }));
    setShowInvoiceSelector(false);
  };
  // Open line item selector to link a line to an invoice line
  const openLineItemSelector = lineIndex => {
    setCurrentLineIndex(lineIndex);
    // Default to the first linked invoice if available
    if (creditNote.linkedInvoices.length > 0) {
      setSelectedInvoiceForLineItems(creditNote.linkedInvoices[0].id);
    }
    setShowLineItemSelector(true);
  };
  // Link a specific invoice line to a credit note line
  const linkLineToInvoiceLine = (invoiceId, invoiceLine) => {
    if (currentLineIndex === null) return;
    // Get the invoice object to access its details
    const invoice = creditNote.linkedInvoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;
    const updatedLines = [...creditNote.lines];
    // Update the line with the linked invoice information
    updatedLines[currentLineIndex] = {
      ...updatedLines[currentLineIndex],
      linkedInvoiceId: invoiceId,
      linkedLineId: invoiceLine.id,
      linkedLineDescription: invoiceLine.description,
      // Optionally pre-fill the credit note line with invoice line details
      description: updatedLines[currentLineIndex].description || `Credit for: ${invoiceLine.description}`,
      unitPrice: updatedLines[currentLineIndex].unitPrice || invoiceLine.unitPrice
    };
    // Calculate the amount based on the updated values
    const lineValue = updatedLines[currentLineIndex].quantity * updatedLines[currentLineIndex].unitPrice;
    updatedLines[currentLineIndex].taxAmount = calculateTaxAmount(lineValue, taxCodes.find(tax => tax.code === updatedLines[currentLineIndex].taxCode));
    updatedLines[currentLineIndex].amount = lineValue + updatedLines[currentLineIndex].taxAmount;
    // Update the credit note state
    setCreditNote(prevState => ({
      ...prevState,
      lines: updatedLines,
      amount: updatedLines.reduce((sum, line) => sum + (line.amount || 0), 0)
    }));
    setShowLineItemSelector(false);
    setCurrentLineIndex(null);
  };
  // Remove a linked invoice
  const removeLinkedInvoice = invoiceId => {
    // First, unlink any credit note lines that are linked to this invoice
    const updatedLines = creditNote.lines.map(line => {
      if (line.linkedInvoiceId === invoiceId) {
        return {
          ...line,
          linkedInvoiceId: null,
          linkedLineId: null,
          linkedLineDescription: null
        };
      }
      return line;
    });
    // Update the credit note state
    setCreditNote(prevState => ({
      ...prevState,
      linkedInvoices: prevState.linkedInvoices.filter(inv => inv.id !== invoiceId),
      lines: updatedLines
    }));
  };
  // Unlink a specific credit note line from its invoice line
  const unlinkLine = lineIndex => {
    const updatedLines = [...creditNote.lines];
    // Remove the link information from the line
    updatedLines[lineIndex] = {
      ...updatedLines[lineIndex],
      linkedInvoiceId: null,
      linkedLineId: null,
      linkedLineDescription: null
    };
    // Update the credit note state
    setCreditNote(prevState => ({
      ...prevState,
      lines: updatedLines
    }));
  };
  // Calculate tax amount based on value and tax code
  const calculateTaxAmount = (value, taxCode) => {
    if (!taxCode) return 0;
    const taxRate = typeof taxCode === 'object' ? taxCode.rate : taxCodes.find(tax => tax.code === taxCode)?.rate || 0;
    return value * taxRate / 100;
  };
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    // Validate required fields
    if (!creditNote.vendor) newErrors.vendor = 'Vendor is required';
    if (!creditNote.creditNoteNumber) newErrors.creditNoteNumber = 'Credit note number is required';
    if (!creditNote.creditNoteDate) newErrors.creditNoteDate = 'Credit note date is required';
    if (!creditNote.currency) newErrors.currency = 'Currency is required';
    // Validate line items
    const lineErrors = [];
    creditNote.lines.forEach((line, index) => {
      const lineError = {};
      if (!line.description) lineError.description = 'Description is required';
      if (line.quantity <= 0) lineError.quantity = 'Quantity must be greater than 0';
      if (Object.keys(lineError).length > 0) {
        lineErrors[index] = lineError;
      }
    });
    if (lineErrors.length > 0) {
      newErrors.lines = lineErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (asDraft = true) => {
    // Only validate if not a draft
    if (!asDraft && !validateForm()) {
      return;
    }
    // Call the save handler from parent
    onSave(creditNote, asDraft);
  };
  // Handle search input keydown event
  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };
  // Get filtered line items based on selected invoice
  const getFilteredLineItems = () => {
    if (!selectedInvoiceForLineItems) return [];
    const selectedInvoice = creditNote.linkedInvoices.find(inv => inv.id === selectedInvoiceForLineItems);
    return selectedInvoice ? selectedInvoice.lines : [];
  };
  // Handle invoice selection change in line item selector
  const handleInvoiceSelectionChange = e => {
    setSelectedInvoiceForLineItems(e.target.value);
  };
  return <div className="mb-8" data-id="element-3345">
      <div className="flex justify-between items-center mb-6" data-id="element-3346">
        <div data-id="element-3347">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3348">
            Credit Note Entry
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3349">
            Create a new credit note, optionally linking to existing invoices
          </p>
        </div>
      </div>

      {/* Credit Note Header Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3350">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('header')} data-id="element-3351">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3352">
            Credit Note Header Information
          </h4>
          {expandedSections.header ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3353" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3354" />}
        </div>
        {expandedSections.header && <div className="px-4 py-5 sm:p-6" data-id="element-3355">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-3356">
              <div className="sm:col-span-2" data-id="element-3357">
                <label htmlFor="creditNoteNumber" className="block text-sm font-medium text-gray-700" data-id="element-3358">
                  Credit Note Number *
                </label>
                <div className="mt-1" data-id="element-3359">
                  <input type="text" name="creditNoteNumber" id="creditNoteNumber" value={creditNote.creditNoteNumber} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.creditNoteNumber ? 'border-red-300' : ''}`} placeholder="Enter credit note number" data-id="element-3360" />
                  {errors.creditNoteNumber && <p className="mt-1 text-sm text-red-600" data-id="element-3361">
                      {errors.creditNoteNumber}
                    </p>}
                </div>
              </div>
              <div data-id="element-3362">
                <label htmlFor="creditNoteDate" className="block text-sm font-medium text-gray-700" data-id="element-3363">
                  Credit Note Date *
                </label>
                <div className="mt-1" data-id="element-3364">
                  <input type="date" name="creditNoteDate" id="creditNoteDate" value={creditNote.creditNoteDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.creditNoteDate ? 'border-red-300' : ''}`} data-id="element-3365" />
                  {errors.creditNoteDate && <p className="mt-1 text-sm text-red-600" data-id="element-3366">
                      {errors.creditNoteDate}
                    </p>}
                </div>
              </div>
              <div data-id="element-3367">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-3368">
                  Currency *
                </label>
                <div className="mt-1" data-id="element-3369">
                  <select id="currency" name="currency" value={creditNote.currency} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.currency ? 'border-red-300' : ''}`} data-id="element-3370">
                    <option value="USD" data-id="element-3371">USD - US Dollar</option>
                    <option value="EUR" data-id="element-3372">EUR - Euro</option>
                    <option value="GBP" data-id="element-3373">GBP - British Pound</option>
                    <option value="AUD" data-id="element-3374">AUD - Australian Dollar</option>
                    <option value="CAD" data-id="element-3375">CAD - Canadian Dollar</option>
                  </select>
                  {errors.currency && <p className="mt-1 text-sm text-red-600" data-id="element-3376">
                      {errors.currency}
                    </p>}
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Vendor Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3377">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('vendor')} data-id="element-3378">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3379">
            Vendor Information
          </h4>
          {expandedSections.vendor ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3380" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3381" />}
        </div>
        {expandedSections.vendor && <div className="px-4 py-5 sm:p-6" data-id="element-3382">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-3383">
              <div className="sm:col-span-2" data-id="element-3384">
                <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-3385">
                  Vendor *
                </label>
                <div className="mt-1" data-id="element-3386">
                  <select id="vendor" name="vendor" value={creditNote.vendor} onChange={e => {
                const selectedVendor = vendors.find(v => v.name === e.target.value);
                setCreditNote({
                  ...creditNote,
                  vendor: e.target.value,
                  vendorId: selectedVendor ? selectedVendor.id : ''
                });
              }} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.vendor ? 'border-red-300' : ''}`} data-id="element-3387">
                    <option value="" data-id="element-3388">Select vendor</option>
                    {vendors.map(vendor => <option key={vendor.id} value={vendor.name} data-id="element-3389">
                        {vendor.name}
                      </option>)}
                  </select>
                  {errors.vendor && <p className="mt-1 text-sm text-red-600" data-id="element-3390">{errors.vendor}</p>}
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-3391">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-3392">
                  Description
                </label>
                <div className="mt-1" data-id="element-3393">
                  <textarea id="description" name="description" rows={3} value={creditNote.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter credit note description" data-id="element-3394" />
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Linked Invoices */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3395">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('linkedInvoices')} data-id="element-3396">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3397">Linked Invoices</h4>
          {expandedSections.linkedInvoices ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3398" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3399" />}
        </div>
        {expandedSections.linkedInvoices && <div className="px-4 py-5 sm:p-6" data-id="element-3400">
            <div className="mb-4 flex justify-end" data-id="element-3401">
              <button type="button" onClick={openInvoiceSelector} disabled={!creditNote.vendorId} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${!creditNote.vendorId ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-3402">
                <LinkIcon className="h-4 w-4 mr-1" data-id="element-3403" />
                Link Invoice
              </button>
            </div>
            {!creditNote.vendorId && <div className="text-center py-2 mb-4 text-sm text-amber-600 bg-amber-50 rounded-md" data-id="element-3404">
                Please select a vendor first to link invoices
              </div>}
            {creditNote.linkedInvoices.length > 0 ? <div className="overflow-x-auto" data-id="element-3405">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3406">
                  <thead className="bg-gray-50" data-id="element-3407">
                    <tr data-id="element-3408">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3409">
                        Invoice Number
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3410">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3411">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3412">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-3413">
                    {creditNote.linkedInvoices.map(invoice => <tr key={invoice.id} data-id="element-3414">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3415">
                          {invoice.number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3416">
                          {invoice.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3417">
                          {formatCurrency(invoice.amount, creditNote.currency, locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3418">
                          <button onClick={() => removeLinkedInvoice(invoice.id)} className="text-red-600 hover:text-red-900" title="Unlink Invoice" data-id="element-3419">
                            <UnlinkIcon className="h-5 w-5" data-id="element-3420" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-3421">
                No invoices linked. Click "Link Invoice" to associate this
                credit note with an invoice.
              </div>}
          </div>}
      </div>

      {/* Line Items */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3422">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('lines')} data-id="element-3423">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3424">Line Items</h4>
          {expandedSections.lines ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3425" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3426" />}
        </div>
        {expandedSections.lines && <div className="px-4 py-5 sm:p-6" data-id="element-3427">
            <div className="mb-4 flex justify-end" data-id="element-3428">
              <button type="button" onClick={addLineItem} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3429">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-3430" />
                Add Line Item
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-3431">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3432">
                <thead className="bg-gray-50" data-id="element-3433">
                  <tr data-id="element-3434">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3435">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3436">
                      Quantity
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3437">
                      Unit Price
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3438">
                      Tax Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3439">
                      Tax Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3440">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3441">
                      Linked To
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3442">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3443">
                  {creditNote.lines.map((line, index) => <tr key={line.id} data-id="element-3444">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3445">
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={line.description} onChange={e => handleLineItemChange(index, 'description', e.target.value)} placeholder="Description" data-id="element-3446" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3447">
                        <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={line.quantity} onChange={e => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="0" step="1" data-id="element-3448" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3449">
                        <div className="relative rounded-md shadow-sm" data-id="element-3450">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3451">
                            <span className="text-gray-500 sm:text-sm" data-id="element-3452">
                              {creditNote.currency === 'USD' ? '$' : creditNote.currency}
                            </span>
                          </div>
                          <input type="text" value={line.unitPrice} onChange={e => {
                      // Remove non-numeric characters except decimal point
                      const sanitizedValue = e.target.value.replace(/[^\d.]/g, '');
                      handleLineItemChange(index, 'unitPrice', parseFloat(sanitizedValue) || 0);
                    }} className="block w-full pl-7 pr-3 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-right" placeholder="0.00" data-id="element-3453" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3454">
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={line.taxCode} onChange={e => handleLineItemChange(index, 'taxCode', e.target.value)} data-id="element-3455">
                          {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-3456">
                              {code.code} - {code.name}
                            </option>)}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3457">
                        {formatCurrency(line.taxAmount, creditNote.currency, locale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3458">
                        {formatCurrency(line.amount, creditNote.currency, locale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3459">
                        {line.linkedLineDescription ? <div className="flex items-center" data-id="element-3460">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full" data-id="element-3461">
                              {line.linkedLineDescription}
                            </span>
                            <button onClick={() => unlinkLine(index)} className="ml-2 text-gray-400 hover:text-gray-600" title="Unlink" data-id="element-3462">
                              <UnlinkIcon className="h-4 w-4" data-id="element-3463" />
                            </button>
                          </div> : <button onClick={() => openLineItemSelector(index)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" disabled={creditNote.linkedInvoices.length === 0} title={creditNote.linkedInvoices.length === 0 ? 'Link an invoice first' : 'Link to invoice line'} data-id="element-3464">
                            <LinkIcon className="h-3 w-3 mr-1" data-id="element-3465" />
                            Link
                          </button>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3466">
                        <button onClick={() => removeLineItem(line.id)} className="text-red-600 hover:text-red-900" title="Remove Line" data-id="element-3467">
                          <TrashIcon className="h-5 w-5" data-id="element-3468" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-50" data-id="element-3469">
                  <tr data-id="element-3470">
                    <td colSpan="5" className="px-6 py-4 text-right text-sm font-medium text-gray-900" data-id="element-3471">
                      Total Amount:
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-3472">
                      {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                    </td>
                    <td colSpan="2" data-id="element-3473"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>}
      </div>

      {/* Notes Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3474">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-3475">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3476">Notes</h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-3477">
          <StandardizedNotes value={creditNote.notes} onChange={value => setCreditNote({
          ...creditNote,
          notes: value
        })} visibility={creditNote.noteVisibility} onVisibilityChange={visibility => setCreditNote({
          ...creditNote,
          noteVisibility: visibility
        })} visibilityDetails={creditNote.noteVisibilityDetails} onVisibilityDetailsChange={details => setCreditNote({
          ...creditNote,
          noteVisibilityDetails: details
        })} label="Credit Note Notes" placeholder="Add any notes about this credit note. Select who can view these notes using the visibility options." data-id="element-3478" />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6" data-id="element-3479">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-3480">
          <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-3" data-id="element-3481">
            <button type="button" onClick={() => handleSubmit(true)} className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3482">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-3483" />
              Save as Draft
            </button>
            <button type="button" onClick={() => handleSubmit(false)} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3484">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-3485" />
              Submit Credit Note
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Selector Modal */}
      {showInvoiceSelector && <div className="fixed inset-0 overflow-y-auto z-50" data-id="element-3486">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3487">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-3488">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-3489"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-3490">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-3491">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3492">
                <div className="sm:flex sm:items-start" data-id="element-3493">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-3494">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-3495">
                      Select Invoice to Link
                    </h3>
                    <div className="mt-2" data-id="element-3496">
                      <div className="mb-4" data-id="element-3497">
                        <div className="flex items-center" data-id="element-3498">
                          <div className="relative flex-grow" data-id="element-3499">
                            <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border pr-10" placeholder="Search invoices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={handleSearchKeyDown} data-id="element-3500" />
                            {searchTerm && <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => {
                          setSearchTerm('');
                          performSearch();
                        }} data-id="element-3501">
                                <XIcon className="h-4 w-4 text-gray-400" data-id="element-3502" />
                              </button>}
                          </div>
                          <button className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={performSearch} data-id="element-3503">
                            <SearchIcon className="h-4 w-4 text-gray-500" data-id="element-3504" />
                            <span className="ml-1" data-id="element-3505">Search</span>
                          </button>
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto" data-id="element-3506">
                        <table className="min-w-full divide-y divide-gray-200" data-id="element-3507">
                          <thead className="bg-gray-50" data-id="element-3508">
                            <tr data-id="element-3509">
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3510">
                                Invoice #
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3511">
                                Date
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3512">
                                Amount
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3513">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200" data-id="element-3514">
                            {isSearching ? <tr data-id="element-3515">
                                <td colSpan={4} className="text-center py-4" data-id="element-3516">
                                  <div className="flex justify-center items-center" data-id="element-3517">
                                    <svg className="animate-spin h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3518">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3519"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3520"></path>
                                    </svg>
                                    <span data-id="element-3521">Searching...</span>
                                  </div>
                                </td>
                              </tr> : filteredInvoices.length > 0 ? filteredInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => linkInvoice(invoice)} data-id="element-3522">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3523">
                                    {invoice.number}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3524">
                                    {invoice.date}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3525">
                                    {formatCurrency(invoice.amount, creditNote.currency, locale)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3526">
                                    <button onClick={e => {
                              e.stopPropagation();
                              linkInvoice(invoice);
                            }} className="text-indigo-600 hover:text-indigo-900" data-id="element-3527">
                                      Link
                                    </button>
                                  </td>
                                </tr>) : <tr data-id="element-3528">
                                <td colSpan={4} className="text-center py-4 text-sm text-gray-500" data-id="element-3529">
                                  {creditNote.vendorId ? searchTerm ? 'No invoices found matching your search.' : 'No available invoices for this vendor.' : 'Please select a vendor first to see available invoices.'}
                                </td>
                              </tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3530">
                <button type="button" onClick={() => {
              setShowInvoiceSelector(false);
              setSearchTerm('');
            }} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-3531">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Line Item Selector Modal */}
      {showLineItemSelector && <div className="fixed inset-0 overflow-y-auto z-50" data-id="element-3532">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3533">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-3534">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-3535"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-3536">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-3537">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3538">
                <div className="sm:flex sm:items-start" data-id="element-3539">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-3540">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-3541">
                      Select Invoice Line to Link
                    </h3>
                    <div className="mt-2" data-id="element-3542">
                      <div className="mb-4" data-id="element-3543">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3544">
                          Select Invoice
                        </label>
                        <select className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={selectedInvoiceForLineItems || ''} onChange={handleInvoiceSelectionChange} data-id="element-3545">
                          <option value="" disabled data-id="element-3546">
                            Select an invoice
                          </option>
                          {creditNote.linkedInvoices.map(invoice => <option key={invoice.id} value={invoice.id} data-id="element-3547">
                              {invoice.number} -{' '}
                              {formatCurrency(invoice.amount, creditNote.currency, locale)}
                            </option>)}
                        </select>
                      </div>
                      <div className="max-h-60 overflow-y-auto" data-id="element-3548">
                        <table className="min-w-full divide-y divide-gray-200" data-id="element-3549">
                          <thead className="bg-gray-50" data-id="element-3550">
                            <tr data-id="element-3551">
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3552">
                                Description
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3553">
                                Quantity
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3554">
                                Unit Price
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3555">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200" data-id="element-3556">
                            {selectedInvoiceForLineItems ? getFilteredLineItems().length > 0 ? getFilteredLineItems().map(line => <tr key={`${selectedInvoiceForLineItems}-${line.id}`} className="hover:bg-gray-50 cursor-pointer" onClick={() => linkLineToInvoiceLine(selectedInvoiceForLineItems, line)} data-id="element-3557">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3558">
                                      {line.description}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3559">
                                      {line.quantity}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3560">
                                      {formatCurrency(line.unitPrice, creditNote.currency, locale)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3561">
                                      <button onClick={e => {
                              e.stopPropagation();
                              linkLineToInvoiceLine(selectedInvoiceForLineItems, line);
                            }} className="text-indigo-600 hover:text-indigo-900" data-id="element-3562">
                                        Link
                                      </button>
                                    </td>
                                  </tr>) : <tr data-id="element-3563">
                                  <td colSpan={4} className="text-center py-4 text-sm text-gray-500" data-id="element-3564">
                                    No line items found for this invoice
                                  </td>
                                </tr> : <tr data-id="element-3565">
                                <td colSpan={4} className="text-center py-4 text-sm text-gray-500" data-id="element-3566">
                                  Please select an invoice to view line items
                                </td>
                              </tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3567">
                <button type="button" onClick={() => {
              setShowLineItemSelector(false);
              setCurrentLineIndex(null);
              setSelectedInvoiceForLineItems(null);
            }} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-3568">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}